import React, { useState, useEffect } from 'react';
import { empresaService, Empresa, EmpresaRequest, EmpresaUpdateRequest } from '../services/api';
import DeleteConfirmationModal from './DeleteConfirmationModal';

interface CompanyManagementProps {
  onSelectCompany: (company: Empresa) => void;
}

const CompanyManagement: React.FC<CompanyManagementProps> = ({ onSelectCompany }) => {
  const [companies, setCompanies] = useState<Empresa[]>([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingCompany, setEditingCompany] = useState<Empresa | null>(null);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    itemName: '',
    onConfirm: () => {},
    isLoading: false
  });
  const [formData, setFormData] = useState<EmpresaRequest>({
    razao: '',
    cnpj: '',
    vencimento: '',
    valor: 0
  });
  const [fieldErrors, setFieldErrors] = useState<{[key: string]: string}>({});

  useEffect(() => {
    loadCompanies();
  }, []);

  const loadCompanies = async () => {
    try {
      setLoading(true);
      const data = await empresaService.getAll();
      setCompanies(data);
    } catch (error) {
      setError('Erro ao carregar empresas');
    } finally {
      setLoading(false);
    }
  };

  const validateCNPJ = (cnpj: string): boolean => {
    // Remove caracteres não numéricos
    const cleanCNPJ = cnpj.replace(/\D/g, '');
    
    if (cleanCNPJ.length !== 14) return false;
    
    // Verifica se todos os dígitos são iguais
    if (/^(\d)\1{13}$/.test(cleanCNPJ)) return false;
    
    // Validação dos dígitos verificadores
    let soma = 0;
    let peso = 2;
    
    // Primeiro dígito verificador
    for (let i = 11; i >= 0; i--) {
      soma += parseInt(cleanCNPJ[i]) * peso;
      peso = peso === 9 ? 2 : peso + 1;
    }
    
    let digito1 = soma % 11 < 2 ? 0 : 11 - (soma % 11);
    
    if (parseInt(cleanCNPJ[12]) !== digito1) return false;
    
    // Segundo dígito verificador
    soma = 0;
    peso = 2;
    
    for (let i = 12; i >= 0; i--) {
      soma += parseInt(cleanCNPJ[i]) * peso;
      peso = peso === 9 ? 2 : peso + 1;
    }
    
    let digito2 = soma % 11 < 2 ? 0 : 11 - (soma % 11);
    
    return parseInt(cleanCNPJ[13]) === digito2;
  };

  const validateField = (name: string, value: string | number) => {
    const errors = { ...fieldErrors };
    
    switch (name) {
      case 'razao':
        if (!value || (typeof value === 'string' && !value.trim())) {
          errors.razao = 'Razão social é obrigatória';
        } else if (typeof value === 'string' && value.length < 3) {
          errors.razao = 'Razão social deve ter pelo menos 3 caracteres';
        } else {
          delete errors.razao;
        }
        break;
      case 'cnpj':
        if (!value || (typeof value === 'string' && !value.trim())) {
          errors.cnpj = 'CNPJ é obrigatório';
        } else if (typeof value === 'string' && !validateCNPJ(value)) {
          errors.cnpj = 'CNPJ inválido';
        } else {
          delete errors.cnpj;
        }
        break;
      case 'vencimento':
        if (!value) {
          errors.vencimento = 'Data de vencimento é obrigatória';
        } else {
          delete errors.vencimento;
        }
        break;
      case 'valor':
        if (typeof value === 'number' && value < 0) {
          errors.valor = 'Valor deve ser maior ou igual a zero';
        } else {
          delete errors.valor;
        }
        break;
    }
    
    setFieldErrors(errors);
  };

  const formatCNPJ = (value: string): string => {
    const cleanValue = value.replace(/\D/g, '');
    return cleanValue
      .replace(/(\d{2})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1/$2')
      .replace(/(\d{4})(\d)/, '$1-$2')
      .substring(0, 18);
  };

  const handleCNPJChange = (value: string) => {
    const formatted = formatCNPJ(value);
    setFormData({ ...formData, cnpj: formatted });
    
    // Validação em tempo real
    setTimeout(() => validateField('cnpj', formatted), 300);
  };

  const handleInputChange = (name: string, value: string | number) => {
    setFormData({ ...formData, [name]: value });
    
    // Validação em tempo real para campos específicos
    if (['razao', 'vencimento', 'valor'].includes(name)) {
      setTimeout(() => validateField(name, value), 300);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    
    // Validações
    if (!formData.razao.trim() || formData.razao.length > 60) {
      setError('Razão social deve ter entre 1 e 60 caracteres');
      return;
    }
    
    const cleanCNPJ = formData.cnpj.replace(/\D/g, '');
    if (!validateCNPJ(cleanCNPJ)) {
      setError('CNPJ inválido');
      return;
    }
    
    if (!formData.vencimento) {
      setError('Data de vencimento é obrigatória');
      return;
    }
    
    if (formData.valor < 0) {
      setError('Valor deve ser maior ou igual a zero');
      return;
    }
    
    try {
      setLoading(true);
      
      const dataToSend = {
        ...formData,
        cnpj: cleanCNPJ,
        vencimento: new Date(formData.vencimento).toISOString()
      };
      
      if (editingCompany) {
        const updateData: EmpresaUpdateRequest = {
          ...dataToSend,
          ativo: editingCompany.ativo
        };
        await empresaService.update(editingCompany.id!, updateData);
        setSuccessMessage('Empresa atualizada com sucesso!');
      } else {
        await empresaService.create(dataToSend);
        setSuccessMessage('Empresa criada com sucesso!');
      }
      
      setShowForm(false);
      setEditingCompany(null);
      setFormData({ razao: '', cnpj: '', vencimento: '', valor: 0 });
      loadCompanies();
      
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error: any) {
      setError(error.response?.data?.message || 'Erro ao salvar empresa');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (company: Empresa) => {
    setEditingCompany(company);
    setFormData({
      razao: company.razao,
      cnpj: formatCNPJ(company.cnpj),
      vencimento: company.vencimento.split('T')[0],
      valor: company.valor
    });
    setShowForm(true);
  };

  const handleToggleActive = async (company: Empresa) => {
    try {
      setLoading(true);
      
      if (company.ativo) {
        // Desativar empresa
        await empresaService.delete(company.id!);
      } else {
        // Ativar empresa
        const updateData: EmpresaUpdateRequest = {
          razao: company.razao,
          cnpj: company.cnpj,
          vencimento: company.vencimento,
          valor: company.valor,
          ativo: true
        };
        await empresaService.update(company.id!, updateData);
      }
      
      loadCompanies();
      setSuccessMessage(`Empresa ${company.ativo ? 'desativada' : 'ativada'} com sucesso!`);
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error: any) {
      setError(error.response?.data?.message || 'Erro ao alterar status da empresa');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (company: Empresa) => {
    setDeleteModal({
      isOpen: true,
      title: 'Excluir Empresa',
      message: 'Tem certeza que deseja excluir esta empresa? Todos os dados associados também serão removidos.',
      itemName: company.razao,
      onConfirm: () => confirmDeleteCompany(company.id!),
      isLoading: false
    });
  };

  const confirmDeleteCompany = async (id: string) => {
    setDeleteModal(prev => ({ ...prev, isLoading: true }));
    try {
      await empresaService.delete(id);
      setSuccessMessage('Empresa excluída com sucesso!');
      loadCompanies();
      setError('');
      setDeleteModal(prev => ({ ...prev, isOpen: false, isLoading: false }));
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error: any) {
      setError(error.response?.data?.message || 'Erro ao excluir empresa');
      setDeleteModal(prev => ({ ...prev, isLoading: false }));
    }
  };

  const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  return (
    <div className="main-content">
      <div className="container-fluid">
        <div className="row">
          <div className="col-12">
            <div className="d-flex justify-content-between align-items-center mb-4">
              <h2>
                <i className="fas fa-building me-2"></i>
                Gerenciar Empresas
              </h2>
              <button
                onClick={() => {
                  setShowForm(true);
                  setEditingCompany(null);
                  setFormData({ razao: '', cnpj: '', vencimento: '', valor: 0 });
                }}
                className="btn btn-primary"
              >
                <i className="fas fa-plus me-2"></i>
                Nova Empresa
              </button>
            </div>

            {successMessage && (
              <div className="alert alert-success alert-dismissible fade show animate__animated animate__fadeInDown animate__pulse" role="alert" style={{transition: 'all 0.3s ease'}}>
                <div className="d-flex align-items-center">
                  <div className="rounded-circle bg-success text-white me-3 d-flex align-items-center justify-content-center animate__animated animate__bounceIn" style={{width: '32px', height: '32px'}}>
                    <i className="fas fa-check fa-sm"></i>
                  </div>
                  <div className="flex-grow-1">
                    <strong>Sucesso!</strong>
                    <div>{successMessage}</div>
                    <div className="mt-1 small text-success-emphasis">
                      <i className="fas fa-info-circle me-1"></i>
                      Os dados foram atualizados automaticamente.
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setSuccessMessage('')}
                ></button>
              </div>
            )}

            {error && (
              <div className="alert alert-danger alert-dismissible fade show animate__animated animate__fadeInDown animate__shakeX" role="alert" style={{transition: 'all 0.3s ease'}}>
                <div className="d-flex align-items-center">
                  <div className="rounded-circle bg-danger text-white me-3 d-flex align-items-center justify-content-center animate__animated animate__pulse" style={{width: '32px', height: '32px'}}>
                    <i className="fas fa-exclamation-triangle fa-sm"></i>
                  </div>
                  <div className="flex-grow-1">
                    <strong>Erro!</strong>
                    <div>{error}</div>
                    <div className="mt-1 small text-danger-emphasis">
                      <i className="fas fa-info-circle me-1"></i>
                      Verifique os dados e tente novamente.
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setError('')}
                ></button>
              </div>
            )}

            {showForm && (
              <div className="card border-0 shadow-sm mb-4">
                <div className="card-header bg-primary text-white">
                  <h5 className="mb-0">
                    <i className={`fas ${editingCompany ? 'fa-edit' : 'fa-plus'} me-2`}></i>
                    {editingCompany ? 'Editar Empresa' : 'Nova Empresa'}
                  </h5>
                </div>
                <div className="card-body">
                  <form onSubmit={handleSubmit}>
                    <div className="row">
                      <div className="col-md-6 mb-3">
                        <label className="form-label">
                          Razão Social <span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          value={formData.razao}
                          onChange={(e) => handleInputChange('razao', e.target.value)}
                          maxLength={60}
                          className={`form-control ${fieldErrors.razao ? 'is-invalid' : ''}`}
                          required
                        />
                        {fieldErrors.razao && (
                          <div className="invalid-feedback">
                            {fieldErrors.razao}
                          </div>
                        )}
                        <div className="form-text">
                          {formData.razao.length}/60 caracteres
                        </div>
                      </div>

                      <div className="col-md-6 mb-3">
                        <label className="form-label">
                          CNPJ <span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          value={formData.cnpj}
                          onChange={(e) => handleCNPJChange(e.target.value)}
                          placeholder="00.000.000/0000-00"
                          className={`form-control ${fieldErrors.cnpj ? 'is-invalid' : ''}`}
                          required
                        />
                        {fieldErrors.cnpj && (
                          <div className="invalid-feedback">
                            {fieldErrors.cnpj}
                          </div>
                        )}
                      </div>

                      <div className="col-md-6 mb-3">
                        <label className="form-label">
                          Data de Vencimento <span className="text-danger">*</span>
                        </label>
                        <input
                          type="date"
                          value={formData.vencimento}
                          onChange={(e) => handleInputChange('vencimento', e.target.value)}
                          className={`form-control ${fieldErrors.vencimento ? 'is-invalid' : ''}`}
                          required
                        />
                        {fieldErrors.vencimento && (
                          <div className="invalid-feedback">
                            {fieldErrors.vencimento}
                          </div>
                        )}
                      </div>

                      <div className="col-md-6 mb-3">
                        <label className="form-label">
                          Valor (R$) <span className="text-danger">*</span>
                        </label>
                        <input
                          type="number"
                          value={formData.valor}
                          onChange={(e) => handleInputChange('valor', parseFloat(e.target.value) || 0)}
                          min="0"
                          step="0.01"
                          className={`form-control ${fieldErrors.valor ? 'is-invalid' : ''}`}
                          required
                        />
                        {fieldErrors.valor && (
                          <div className="invalid-feedback">
                            {fieldErrors.valor}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="d-flex gap-2">
                      <button
                        type="submit"
                        disabled={loading}
                        className={`btn btn-primary ${loading ? 'animate__animated animate__pulse animate__infinite' : ''}`}
                        style={{transition: 'all 0.3s ease'}}
                      >
                        {loading ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            {editingCompany ? 'Atualizando...' : 'Cadastrando...'}
                          </>
                        ) : (
                          <>
                            <i className={`fas ${editingCompany ? 'fa-save' : 'fa-plus'} me-2`}></i>
                            {editingCompany ? 'Atualizar' : 'Cadastrar'}
                          </>
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setShowForm(false);
                          setEditingCompany(null);
                          setError('');
                          setFieldErrors({});
                        }}
                        className="btn btn-secondary"
                        disabled={loading}
                        style={{transition: 'all 0.3s ease'}}
                      >
                        <i className="fas fa-times me-2"></i>
                        Cancelar
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}

            {loading && !showForm && (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Carregando...</span>
                </div>
                <p className="mt-3 text-muted">Carregando empresas...</p>
              </div>
            )}

            {!loading && companies.length === 0 && (
              <div className="card border-0 shadow-sm">
                <div className="card-body text-center py-5">
                  <i className="fas fa-building text-muted mb-3" style={{ fontSize: '3rem' }}></i>
                  <h5 className="text-muted">Nenhuma empresa cadastrada</h5>
                  <p className="text-muted mb-0">Clique em "Nova Empresa" para começar</p>
                </div>
              </div>
            )}

            {!loading && companies.length > 0 && (
              <div className="card border-0 shadow-sm">
                <div className="card-header bg-light">
                  <h6 className="mb-0">
                    <i className="fas fa-list me-2"></i>
                    Empresas Cadastradas ({companies.length})
                  </h6>
                </div>
                <div className="card-body p-0">
                  <div className="table-responsive">
                    <table className="table table-hover mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>Empresa</th>
                          <th>CNPJ</th>
                          <th>Vencimento</th>
                          <th>Valor</th>
                          <th>Status</th>
                          <th style={{width: '150px'}}>Ações</th>
                        </tr>
                      </thead>
                      <tbody>
                        {companies.map((company) => (
                          <tr
                            key={company.id}
                            className={`cursor-pointer ${!company.ativo ? 'table-secondary' : ''}`}
                            onClick={() => onSelectCompany(company)}
                            style={{ cursor: 'pointer' }}
                          >
                            <td>
                              <div className="fw-bold">{company.razao}</div>
                            </td>
                            <td className="text-muted">
                              {formatCNPJ(company.cnpj)}
                            </td>
                            <td className="text-muted">
                              {formatDate(company.vencimento)}
                            </td>
                            <td className="text-muted">
                              {formatCurrency(company.valor)}
                            </td>
                            <td>
                              <span
                                className={`badge ${
                                  company.ativo ? 'bg-success' : 'bg-danger'
                                }`}
                              >
                                {company.ativo ? 'Ativa' : 'Inativa'}
                              </span>
                            </td>
                            <td>
                              <div className="btn-group btn-group-sm">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleEdit(company);
                                  }}
                                  className="btn btn-outline-primary"
                                  title="Editar"
                                >
                                  <i className="fas fa-edit"></i>
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleToggleActive(company);
                                  }}
                                  className={`btn ${
                                    company.ativo ? 'btn-outline-warning' : 'btn-outline-success'
                                  }`}
                                  title={company.ativo ? 'Desativar' : 'Ativar'}
                                >
                                  <i className={`fas ${
                                    company.ativo ? 'fa-ban' : 'fa-check'
                                  }`}></i>
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDelete(company);
                                  }}
                                  className="btn btn-outline-danger"
                                  title="Excluir"
                                >
                                  <i className="fas fa-trash"></i>
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            <DeleteConfirmationModal
               isOpen={deleteModal.isOpen}
               title={deleteModal.title}
               message={deleteModal.message}
               itemName={deleteModal.itemName}
               onConfirm={deleteModal.onConfirm}
               onClose={() => setDeleteModal(prev => ({ ...prev, isOpen: false }))}
               isLoading={deleteModal.isLoading}
             />
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompanyManagement;